import React, { useState } from 'react';
import {
  NumberFormatValues,
  NumericFormat,
  NumericFormatProps,
  SourceInfo,
} from 'react-number-format';
import { Gwei } from '../../lib/gwei';
import { add, clamp, subtract } from '../../lib/math';
import { InputInner } from '../catalyst-ui-kit/input';

type BaseNumericFormatProps = Omit<NumericFormatProps, 'placeholder'>;

export type CustomNumericFormatProps = BaseNumericFormatProps & {
  step?: string | number;
  ref?: React.Ref<HTMLInputElement>;
};

export const CustomNumericFormat = ({
  step,
  onKeyDown,
  onBlur,
  onValueChange,
  value,
  ref,
  ...props
}: CustomNumericFormatProps) => {
  const [tempValue, setTempValue] = useState<NumberFormatValues | undefined>(undefined);

  // If `step` is provided, handle arrow keys for stepping
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(event);
    }

    if (event.defaultPrevented || !step) {
      return;
    }

    const currentValue = tempValue?.value || value;

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();

      // Add or subtract `step` based on key.
      const operation = event.key === 'ArrowUp' ? add : subtract;
      const steppedGwei = operation(Gwei.toGwei(currentValue || 0), Gwei.toGwei(step));
      // Clamp to `min` and `max` props.
      const clamped = Gwei.fmt(
        clamp(steppedGwei, {
          min: props.min !== undefined ? Gwei.toGwei(props.min) : undefined,
          max: props.max !== undefined ? Gwei.toGwei(props.max) : undefined,
        })
      );

      const newValue = {
        value: clamped,
        floatValue: Number(clamped),
        formattedValue: clamped,
      };

      // Check if we should use tempValue for this stepped value
      if (
        (clamped.includes('.') && clamped.endsWith('0')) ||
        clamped.endsWith('.') ||
        clamped === '-'
      ) {
        setTempValue(newValue);
      } else {
        if (tempValue) {
          setTempValue(undefined);
        }
        onValueChange?.(newValue, { source: 'event' as SourceInfo['source'] });
      }

      // Move cursor to the end after update
      setTimeout(() => {
        if (ref && 'current' in ref && ref.current) {
          // Prefix length doesn't needed to be included.
          const length = ref.current.value.length - (props.suffix?.length ?? 0);
          ref.current.setSelectionRange(length, length);
        }
      }, 1);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (tempValue) {
      onValueChange?.(tempValue, { source: 'event' as SourceInfo['source'] });
      setTempValue(undefined);
    }
    onBlur?.(e);
  };

  const handleValueChange = (valueData: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.source !== 'event') return;

    if (
      (valueData.value.includes('.') && valueData.value.endsWith('0')) ||
      valueData.value.endsWith('.') ||
      valueData.value === '-'
    ) {
      setTempValue(valueData);
      return;
    }

    if (tempValue) {
      setTempValue(undefined);
    }
    onValueChange?.(valueData, sourceInfo);
  };

  return (
    <NumericFormat
      getInputRef={ref}
      isAllowed={(values) => {
        const { floatValue = 0 } = values;
        if (floatValue >= Number.MAX_SAFE_INTEGER) {
          return false;
        }
        if (props.isAllowed) {
          return props.isAllowed(values);
        }
        return true;
      }}
      thousandSeparator={true}
      decimalScale={9}
      fixedDecimalScale={false}
      allowNegative={false}
      autoComplete="off"
      customInput={InputInner}
      id={props.id || props.name}
      value={tempValue?.value || value}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onValueChange={handleValueChange}
      step={step}
      {...props}
    />
  );
};
